<script setup lang="ts">
import { defineProps, defineEmits, withDefaults } from "vue";
import CancelIcon from "@/components/icons/CancelIcon.vue";

withDefaults(
  defineProps<{
    show: boolean;
    showCancelIcon?: boolean;
    variant?: "center" | "fullscreen";
    containerClass?: string;
    maxModalSizeClass?: string;
  }>(),
  {
    showCancelIcon: true,
    variant: "center",
    containerClass: "bg-white",
    maxModalSizeClass: "max-w-sm",
  }
);

const emit = defineEmits(["close"]);

const closeModal = () => {
  emit("close");
};
</script>

<template>
  <Teleport to="body">
    <!-- Overlay Transition -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="show"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
        :class="[variant === 'center' ? 'p-4' : 'pt-20']"
        @click.self="closeModal"
      >
        <!-- Modal Body Transition -->
        <transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-if="show"
            class="rounded-lg w-full p-4"
            :class="[
              containerClass,
              variant === 'center' ? maxModalSizeClass : 'h-full flex flex-col',
            ]"
          >
            <div
              class="flex items-center mb-4"
              :class="{ 'justify-between': showCancelIcon }"
            >
              <slot name="header">
                <div></div>
              </slot>
              <button
                v-if="showCancelIcon"
                @click="closeModal"
                class="w-[30px] h-[30px] bg-ternary rounded-full shrink-0"
              >
                <CancelIcon class="text-brown mx-auto" />
              </button>
            </div>
            <!-- Modal Content Slot -->
            <slot name="content"></slot>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped lang="scss"></style>
